import { useEffect, useRef, useState, useCallback } from 'react';
import QrScanner from 'qr-scanner';
import { playAudioFeedback } from '../utils/audioFeedback';
import { getPreferredCameraIndex } from '../utils/cameraSelection';

// Import the barcode detector polyfill
declare global {
  interface Window {
    BarcodeDetector?: any;
  }
}

export interface BarcodeScanResult {
  rawValue: string;
  format: string;
}

export type CameraStatus = 'idle' | 'initializing' | 'ready' | 'error';

interface UseBarcodeScanner {
  cameraStatus: CameraStatus;
  startCamera: () => Promise<void>;
  stopCamera: () => Promise<void>;
  scanBarcode: () => Promise<void>;
  videoRef: React.RefObject<HTMLVideoElement>;
  selectRef: React.RefObject<HTMLSelectElement>;
  error: string | null;
  isScanning: boolean;
}

export const useBarcodeScanner = (
  onScan: (result: BarcodeScanResult) => void | Promise<void>
): UseBarcodeScanner => {
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const barcodeDetectorRef = useRef<any>(null);
  const useNativeDetectorRef = useRef<boolean>(false);
  const isDecodingRef = useRef(false);

  // Initialize barcode detector
  const initBarcodeDetector = useCallback(async () => {
    try {
      // Try native BarcodeDetector first (optional)
      if ('BarcodeDetector' in window) {
        try {
          const nativeSupportedFormats = await window.BarcodeDetector!.getSupportedFormats();
          if (nativeSupportedFormats.includes('qr_code')) {
            barcodeDetectorRef.current = new window.BarcodeDetector!({ formats: ['qr_code'] });
            useNativeDetectorRef.current = true;
            return;
          }
        } catch (nativeErr) {
          // Fall through to QR Scanner
        }
      }

      // QR Scanner library doesn't need initialization - it's all static methods
      useNativeDetectorRef.current = false;
    } catch (err) {
      throw new Error('Nepodarilo sa inicializovať skener čiarových kódov');
    }
  }, []);

  // Stop all camera tracks
  const stopAllTracks = useCallback(async () => {
    if (localStreamRef.current) {
      const tracks = localStreamRef.current.getTracks();
      tracks.forEach(track => track.stop());

      // Wait for tracks to actually stop
      await Promise.all(tracks.map(track => {
        return new Promise<void>(resolve => {
          if (track.readyState === 'ended') {
            resolve();
          } else {
            track.addEventListener('ended', () => resolve(), { once: true });
            setTimeout(resolve, 500); // Fallback timeout
          }
        });
      }));

      localStreamRef.current = null;
    }
  }, []);

  // Load camera devices and start stream
  const loadDevicesAndPlay = useCallback(async (deviceId?: string): Promise<void> => {
    const constraints: MediaStreamConstraints = deviceId
      ? {
          video: {
            deviceId: { exact: deviceId },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            aspectRatio: { ideal: 16 / 9 },
          },
          audio: false,
        }
      : {
          video: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            aspectRatio: { ideal: 16 / 9 },
          },
          audio: false,
        };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // If we're enumerating devices (no deviceId), we need to stop this temp stream
      if (!deviceId && selectRef.current) {
        const tempStream = stream;
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(d => d.kind === 'videoinput');

        // Populate select dropdown
        selectRef.current.innerHTML = '';
        let defaultIndex = 0;

        videoDevices.forEach((device, index) => {
          const option = document.createElement('option');
          option.value = device.deviceId;
          option.text = device.label || `Camera ${index + 1}`;
          selectRef.current!.appendChild(option);
        });

        // Stop temporary stream
        tempStream.getTracks().forEach(track => track.stop());

        if (videoDevices.length === 0) {
          throw new Error('Nenašla sa žiadna kamera');
        }

        defaultIndex = getPreferredCameraIndex(videoDevices);

        // Select default camera and start it
        selectRef.current.selectedIndex = defaultIndex;

        // Small delay for iOS
        await new Promise(resolve => setTimeout(resolve, 100));
        return loadDevicesAndPlay(videoDevices[defaultIndex].deviceId);
      }

      // Set stream to video element
      localStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      setCameraStatus('ready');
    } catch (err: any) {
      // Provide user-friendly error messages
      let errorMessage = 'Nepodarilo sa spustiť kameru';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Prístup k kamere bol zamietnutý. Povoľte prístup v nastaveniach prehliadača.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'Nenašla sa žiadna kamera';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Kamera je používaná inou aplikáciou';
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage = 'Kamera nepodporuje požadované nastavenia';
      } else if (err.message) {
        errorMessage = err.message;
      }

      const enhancedError = new Error(errorMessage);
      enhancedError.name = err.name;
      throw enhancedError;
    }
  }, []);

  // Start camera
  const startCamera = useCallback(async () => {
    setCameraStatus('initializing');
    setError(null);

    try {
      // Initialize barcode detector if needed
      if (!barcodeDetectorRef.current) {
        await initBarcodeDetector();
      }

      // Load devices and start camera
      await loadDevicesAndPlay();
    } catch (err: any) {
      setError(err.message || 'Nepodarilo sa spustiť kameru');
      setCameraStatus('idle'); // Reset to idle instead of error so user can retry
      throw err;
    }
  }, [initBarcodeDetector, loadDevicesAndPlay]);

  // Stop camera
  const stopCamera = useCallback(async () => {
    try {
      await stopAllTracks();
      setCameraStatus('idle');
      setError(null);
    } catch (err) {
      // Silently handle stop errors
    }
  }, [stopAllTracks]);

  // Scan barcode from current video frame (with multiple attempts)
  const scanBarcode = useCallback(async () => {
    if (isDecodingRef.current || !videoRef.current) {
      return;
    }

    isDecodingRef.current = true;
    setIsScanning(true);
    setError(null); // Clear any previous errors

    try {
      // Capture the frame to canvas
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Could not get canvas context');
      }
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

      let foundBarcodes: any[] = [];

      // Use native detector if available
      if (useNativeDetectorRef.current && barcodeDetectorRef.current) {
        try {
          foundBarcodes = await barcodeDetectorRef.current.detect(videoRef.current);
        } catch (nativeErr) {
          // Fall through to QR Scanner
        }
      }

      // Try QR Scanner library (either as primary or fallback)
      if (foundBarcodes.length === 0) {
        try {
          const result = await QrScanner.scanImage(canvas, {
            returnDetailedScanResult: true,
            alsoTryWithoutScanRegion: true,
          });
          foundBarcodes = [{
            rawValue: result.data,
            format: 'qr_code',
          }];
        } catch (canvasErr) {
          // Try from video element
          try {
            const result = await QrScanner.scanImage(videoRef.current, {
              returnDetailedScanResult: true,
              alsoTryWithoutScanRegion: true,
            });
            foundBarcodes = [{
              rawValue: result.data,
              format: 'qr_code',
            }];
          } catch (videoErr) {
            // Detection failed
          }
        }
      }

      if (foundBarcodes.length === 1) {
        const result: BarcodeScanResult = {
          rawValue: foundBarcodes[0].rawValue,
          format: foundBarcodes[0].format,
        };
        await onScan(result);
      } else if (foundBarcodes.length > 1) {
        playAudioFeedback('error');
        setError(`Našlo sa ${foundBarcodes.length} kódov - nasmerujte kameru len na jeden kód`);
      } else {
        playAudioFeedback('error');
        setError('Kód sa nepodarilo prečítať - skúste nasmerovať kameru priamo na QR kód');
      }
    } catch (err) {
      playAudioFeedback('error');
      setError('Chyba pri skenovaní - skúste to znova');
    } finally {
      isDecodingRef.current = false;
      setIsScanning(false);
    }
  }, [onScan]);

  // Handle camera select change
  useEffect(() => {
    const select = selectRef.current;
    if (!select) return;

    const handleChange = async () => {
      if (cameraStatus === 'ready') {
        try {
          await stopAllTracks();
          await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for iOS
          await loadDevicesAndPlay(select.value);
        } catch (err: any) {
          setError(err.message || 'Nepodarilo sa prepnúť kameru');
          setCameraStatus('idle');
        }
      }
    };

    select.addEventListener('change', handleChange);
    return () => select.removeEventListener('change', handleChange);
  }, [cameraStatus, loadDevicesAndPlay, stopAllTracks]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllTracks();
    };
  }, [stopAllTracks]);

  return {
    cameraStatus,
    startCamera,
    stopCamera,
    scanBarcode,
    videoRef,
    selectRef,
    error,
    isScanning,
  };
};
